import React, { useState, useEffect } from 'react';
import { presetsAPI } from '../api';
import { Plus, Trash2, Save, ChevronDown, Lock } from 'lucide-react';

const DEFAULT_TEMPLATE = `Dear {name},
Here are your test results from {schoolName}:

Subject: {subject}
Test Date: {testDate}
Total Marks: {totalMarks}
Obtained Marks: {obtainedMarks}
Highest in Class: {highestMarks}
Percentage: {percentage}
Remark: {remark}

Thank you`;

const VARIABLES = ['{name}', '{schoolName}', '{subject}', '{testDate}', '{totalMarks}', '{obtainedMarks}', '{highestMarks}', '{percentage}', '{remark}'];

export default function MessagePreset({ selectedPresetId, onPresetChange, onTemplateChange, schoolName }) {
  const [presets, setPresets] = useState([]);
  const [selectedId, setSelectedId] = useState(selectedPresetId || null);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [newName, setNewName] = useState('');
  const [showNewInput, setShowNewInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPresets();
  }, [schoolName]);

  const loadPresets = async () => {
    try {
      const res = await presetsAPI.getAll(schoolName || '');
      // n8n returns array directly, or backend returns array
      let data = Array.isArray(res.data) ? res.data : (res.data?.list || res.data?.presets || []);
      
      // Fix: if template is empty/invalid, use default
      data = data.map(p => ({
        ...p,
        template: (p.template && p.template.length > 5) ? p.template : DEFAULT_TEMPLATE,
      }));

      // Add default preset if none exist or all have bad templates
      if (data.length === 0) {
        data.push({ id: 'default', name: 'Default', template: DEFAULT_TEMPLATE, is_default: true });
      }
      
      setPresets(data);
      const def = data.find((p) => p.is_default) || data[0];
      if (def) {
        setSelectedId(def.id);
        setTemplate(def.template || DEFAULT_TEMPLATE);
        onPresetChange && onPresetChange(def.id);
        onTemplateChange && onTemplateChange(def.template || DEFAULT_TEMPLATE);
      }
    } catch (err) {
      console.error('Failed to load presets:', err);
      // Fallback - show default preset even if API fails
      const fallback = [{ id: 'default', name: 'Default', template: DEFAULT_TEMPLATE, is_default: true }];
      setPresets(fallback);
      setSelectedId('default');
      setTemplate(DEFAULT_TEMPLATE);
      onTemplateChange && onTemplateChange(DEFAULT_TEMPLATE);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = (id) => {
    const preset = presets.find((p) => p.id === parseInt(id));
    if (preset) {
      setSelectedId(preset.id);
      setTemplate(preset.template);
      onPresetChange && onPresetChange(preset.id);
      onTemplateChange && onTemplateChange(preset.template);
    }
  };

  const handleTemplateChange = (val) => {
    setTemplate(val);
    onTemplateChange && onTemplateChange(val);
  };

  const handleSaveNew = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const res = await presetsAPI.create({ name: newName.trim(), template, school: schoolName || '' });
      setPresets((prev) => [...prev, res.data]);
      setSelectedId(res.data.id);
      onPresetChange && onPresetChange(res.data.id);
      setNewName('');
      setShowNewInput(false);
    } catch (err) {
      alert('Failed to save preset: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const preset = presets.find((p) => p.id === id);
    if (preset?.is_default) {
      alert('Cannot delete the default preset');
      return;
    }
    if (!window.confirm(`Delete preset "${preset?.name}"?`)) return;
    try {
      await presetsAPI.delete(id);
      const updated = presets.filter((p) => p.id !== id);
      setPresets(updated);
      if (selectedId === id) {
        const fallback = updated.find((p) => p.is_default) || updated[0];
        if (fallback) {
          setSelectedId(fallback.id);
          setTemplate(fallback.template);
          onPresetChange && onPresetChange(fallback.id);
          onTemplateChange && onTemplateChange(fallback.template);
        }
      }
    } catch (err) {
      alert('Failed to delete preset: ' + (err.response?.data?.error || err.message));
    }
  };

  const selectedPreset = presets.find((p) => p.id === selectedId);
  const isDefaultSelected = selectedPreset?.is_default;

  const insertVariable = (variable) => {
    const textarea = document.getElementById('template-textarea');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newVal = template.substring(0, start) + variable + template.substring(end);
      handleTemplateChange(newVal);
      setTimeout(() => {
        textarea.selectionStart = start + variable.length;
        textarea.selectionEnd = start + variable.length;
        textarea.focus();
      }, 10);
    } else {
      handleTemplateChange(template + variable);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-2">
        <span className="font-semibold text-gray-700 text-sm">Message Template</span>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Preset selector */}
          <div className="relative">
            <select
              value={selectedId || ''}
              onChange={(e) => handleSelectPreset(e.target.value)}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-3 pr-8 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-blue-500 cursor-pointer"
              disabled={loading}
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} {p.is_default ? '(Default)' : ''}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          {/* Delete button (non-default only) */}
          {selectedPreset && !isDefaultSelected && (
            <button
              onClick={() => handleDelete(selectedId)}
              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
              title="Delete preset"
            >
              <Trash2 size={14} />
            </button>
          )}

          {isDefaultSelected && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Lock size={11} />
              Read-only
            </span>
          )}

          {/* Add New button */}
          <button
            onClick={() => setShowNewInput(!showNewInput)}
            className="btn-3d-blue py-1.5 px-3 text-xs flex items-center gap-1"
          >
            <Plus size={12} />
            Add New
          </button>
        </div>
      </div>

      {/* Save as new preset input */}
      {showNewInput && (
        <div className="px-4 py-3 border-b border-gray-100 bg-blue-50 flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New preset name..."
            className="flex-1 border border-blue-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && handleSaveNew()}
          />
          <button
            onClick={handleSaveNew}
            disabled={saving || !newName.trim()}
            className="btn-3d-green py-1.5 px-3 text-xs flex items-center gap-1 disabled:opacity-50"
          >
            <Save size={12} />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => { setShowNewInput(false); setNewName(''); }}
            className="text-sm text-gray-500 hover:text-gray-700 px-2"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Variable chips - only show when editing (non-default) */}
      {!isDefaultSelected && (
        <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-1.5">
          <span className="text-xs text-gray-500 self-center mr-1">Insert:</span>
          {VARIABLES.map((v) => (
            <button
              key={v}
              onClick={() => insertVariable(v)}
              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-2 py-0.5 rounded-md font-mono transition-colors"
            >
              {v}
            </button>
          ))}
        </div>
      )}

      {/* Template textarea */}
      <div className="p-4">
        <textarea
          id="template-textarea"
          value={template}
          onChange={(e) => handleTemplateChange(e.target.value)}
          readOnly={isDefaultSelected}
          rows={10}
          className={`w-full border rounded-lg p-3 text-sm font-mono resize-y focus:outline-none transition-colors ${
            isDefaultSelected
              ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed'
              : 'bg-white border-gray-300 focus:border-blue-500 text-gray-800'
          }`}
          placeholder="Enter your message template..."
        />
        {isDefaultSelected && (
          <p className="mt-1.5 text-xs text-gray-400 flex items-center gap-1">
            <Lock size={11} />
            Default preset is read-only. Click "+ Add New" to create an editable copy.
          </p>
        )}
      </div>
    </div>
  );
}
